import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle, Clock, Globe, Star, ArrowRight, CheckCircle } from 'lucide-react';

export default function ContactUsPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 px-6 py-24">
        {/* Floating Background Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full -translate-y-48 translate-x-48 opacity-30 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-cyan-200 to-blue-200 rounded-full translate-y-40 -translate-x-40 opacity-30 animate-pulse delay-1000"></div>
        
        <div className="max-w-6xl mx-auto text-center relative">
          <div className="mt-10 inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 rounded-full px-6 py-3 mb-8">
            <MessageCircle className="w-5 h-5 text-blue-600" />
            <span className="text-blue-700 font-semibold">Let's Connect</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Ready to Start
            <span className="relative inline-block mx-4">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Something
              </span>
              <div className="absolute -bottom-3 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-full"></div>
            </span>
            Amazing?
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-12 leading-tight font-poppins mt-12">
            Transform your vision into reality. Our team of experts is ready to bring your ideas to life with innovative solutions that drive results.
          </p>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto font-poppins">
            {[
              { icon: Clock, text: "< 24h Response", color: "text-green-600" },
              { icon: Star, text: "99% Satisfaction", color: "text-yellow-600" },
              { icon: Globe, text: "Global Support", color: "text-blue-600" },
              { icon: CheckCircle, text: "Free Consultation", color: "text-purple-600" }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center gap-2 bg-white/60 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/80 transition-all duration-300 hover:scale-105">
                <item.icon className={`w-6 h-6 ${item.color}`} />
                <span className="text-gray-700 font-semibold text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            
            {/* Contact Form */}
            <div className="relative font-poppins">
              <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100 relative overflow-hidden">
                {/* Decorative gradient */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                
                <div className="relative">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Send us a message</h2>
                  <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>

                  {isSubmitted && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-green-700 font-semibold">Message sent successfully! We'll be in touch soon.</span>
                    </div>
                  )}

                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-gray-700 font-semibold mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 group-hover:border-gray-300"
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      
                      <div className="group">
                        <label className="block text-gray-700 font-semibold mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 group-hover:border-gray-300"
                          placeholder="john@company.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-gray-700 font-semibold mb-2">Company</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 group-hover:border-gray-300"
                          placeholder="Your Company"
                        />
                      </div>
                      
                      <div className="group">
                        <label className="block text-gray-700 font-semibold mb-2">Service Interested</label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 group-hover:border-gray-300"
                        >
                          <option value="">Select a service</option>
                          <option value="web-development">Web Development</option>
                          <option value="mobile-app">Mobile App Development</option>
                          <option value="ai-solutions">AI Solutions</option>
                          <option value="consulting">Business Consulting</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="group">
                      <label className="block text-gray-700 font-semibold mb-2">Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows="6"
                        className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 resize-none group-hover:border-gray-300"
                        placeholder="Tell us about your project and how we can help you achieve your goals..."
                        required
                      ></textarea>
                    </div>

                    <button
                      onClick={handleSubmit}
                      className="group w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3"
                    >
                      <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      Send Message
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Get in touch</h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-8 font-poppins">
                  We'd love to hear from you. Choose the most convenient way to reach out, and our team will respond promptly to discuss your project.
                </p>
              </div>

              {/* Contact Methods */}
              <div className="space-y-6">
                {[
                  {
                    icon: Mail,
                    title: "Email Us",
                    content: "sales@tsgce-sa.com",
                    description: "Send us an email anytime",
                    color: "from-blue-500 to-cyan-500",
                    bgColor: "from-blue-50 to-cyan-50"
                  },
                  {
                    icon: Phone,
                    title: "Call Us",
                    content: "+055 350 0531",
                    description: "Sat-Thu from 8am to 8pm",
                    color: "from-green-500 to-emerald-500",
                    bgColor: "from-green-50 to-emerald-50"
                  },
                  {
                    icon: MapPin,
                    title: "Visit Us",
                    content: "P.O Box 34426 - Khobar 31952, Kingdom of Saudi Arabia",
                    description: "Al-Khobar, KSA",
                    color: "from-purple-500 to-pink-500",
                    bgColor: "from-purple-50 to-pink-50"
                  }
                ].map((item, index) => (
                  <div key={index} className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 overflow-hidden font-poppins">
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.bgColor} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                    
                    <div className="relative flex items-start gap-4">
                      <div className={`w-12 h-12 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{item.title}</h3>
                        <p className="text-gray-900 font-semibold mb-1">{item.content}</p>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Banner */}
              <div className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 rounded-2xl p-8 text-center overflow-hidden font-poppins">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 rounded-full opacity-10 -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500 rounded-full opacity-10 translate-y-12 -translate-x-12"></div>
                
                <div className="relative">
                  <h3 className="text-2xl font-bold text-white mb-3">Need immediate assistance?</h3>
                  <p className="text-gray-300 mb-6">Schedule a free 30-minute consultation call with our experts.</p>
                  
                  <button className="group bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold px-8 py-3 rounded-full shadow-xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto">
                    Book Free Call
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview Section */}
      <section className="py-16 px-6 bg-gray-50 font-poppins">
        <div className="max-w-6xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
          <p className="text-lg text-gray-600 mb-12">Quick answers to common questions about our services and process.</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                question: "How long does a typical project take?",
                answer: "Most projects are completed within 4-8 weeks, depending on complexity and scope."
              },
              {
                question: "Do you offer ongoing support?",
                answer: "Yes, we provide 24/7 support and maintenance packages for all our clients."
              },
              {
                question: "What's your development process?",
                answer: "We follow an agile methodology with regular updates and client feedback loops."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <h4 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h4>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}